import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {Store} from '@ngrx/store';
import * as fromUser from '../../../store/reducers/user';
import {map} from 'rxjs/operators';
import {IDropDownCurrentUser} from '../../models/users/users.models';
import {
  BOARDS_LINK,
  FEEDBACK_LINK,
  FLEXI_NSNF_LINK,
  FOREIGN_TRAVEL_LINK,
  REPORT_AN_ISSUE_LINK,
  REQUEST_SALES_PROGRESSION_SUPPORT_LINK,
  SUGGEST_IMPROVEMENTS_LINK,
  WHO_CAN_HELP_ME_LINK
} from '../../../core/constants/links.constants';
import {PermissionService} from '../../../core/services/permission/permission.service';
import {AuthPermissionEnum, UserDropdownPermissionEnum} from '../../enums/permission.enum';


@Component({
  selector: 'app-user-dropdown',
  templateUrl: './user-dropdown.component.html',
  styleUrls: ['./user-dropdown.component.scss']
})
export class UserDropdownComponent implements OnInit, OnDestroy {
  readonly userDropdownId: string = 'userDropdownMenuParent';
  private dropdownEl: HTMLElement;
  public isDropdownActive: boolean;
  currentUser: IDropDownCurrentUser;

  suggestImprovementLink = SUGGEST_IMPROVEMENTS_LINK;
  reportAnIssueLink = REPORT_AN_ISSUE_LINK;
  whoCanHelpMeLink = WHO_CAN_HELP_ME_LINK;
  feedbackLink = FEEDBACK_LINK;
  flexiNSNFLink = FLEXI_NSNF_LINK;
  boardsLink = BOARDS_LINK;
  foreignTravelLink = FOREIGN_TRAVEL_LINK;
  requestSalesProgressionSupportLink = REQUEST_SALES_PROGRESSION_SUPPORT_LINK;

  get name(): string {
    return this.currentUser.name;
  }

  get userAvatar(): string {
    return Array.isArray(this.currentUser.uploads) ? this.currentUser.uploads[0]?.fileName : null;
  }

  get canImpersonate(): boolean {
    const canImpersonateLegacy = this.permissions.hasOneOfPermissions({
      permissions: [
        AuthPermissionEnum.AUTH_IMPERSONATE_ALL,
        AuthPermissionEnum.AUTH_IMPERSONATE_RELATED,
      ]
    });

    // Branch-agent impersonation also requires the agent to be enabled in at least one branch
    // (the permission is a static capability; the per-branch flag is the real toggle).
    const canImpersonateBranchAgent =
      this.permissions.hasPermission(AuthPermissionEnum.AUTH_IMPERSONATE_BRANCH_AGENT_RELATED)
      && !!this.currentUser?.branchAgentImpersonateEnabled;

    return canImpersonateLegacy || canImpersonateBranchAgent;
  }

  get canRequestSalesProgressionSupport(): boolean {
    return this.permissions.hasPermission(UserDropdownPermissionEnum.MENU_REQUEST_SALES_PROGRESSION_SUPPORT);
  }

  constructor(private auth: AuthenticationService,
              private store: Store,
              private permissions: PermissionService) {
  }

  ngOnInit(): void {
    this.addBootstrapEventListeners();
    this.store.select(fromUser.selectCurrentUserState)
      .pipe(map(user => ({
        name: user?.details?.name,
        uploads: user?.details?.uploads,
        branchAgentImpersonateEnabled: user?.details?.branchAgentImpersonateEnabled
      })))
      .subscribe(currentUser => {
        this.currentUser = currentUser;
      });
  }

  ngOnDestroy(): void {
    this.destroyBootstrapEventListeners();
  }

  /**
   * Listens on bootstrap dropdown show/hide events.
   * isDropdownActive adds class to main parent element for styling
   */
  private addBootstrapEventListeners(): void {
    this.dropdownEl = document.getElementById(this.userDropdownId);
    this.dropdownEl.addEventListener('show.bs.dropdown', this.setActiveClassToDropdown);
    this.dropdownEl.addEventListener('hide.bs.dropdown', this.removeActiveClassFromDropdown);
  }

  /**
   * Destroys bootstrap events listeners.
   */
  private destroyBootstrapEventListeners(): void {
    this.dropdownEl.removeEventListener('show.bs.dropdown', this.setActiveClassToDropdown);
    this.dropdownEl.removeEventListener('hide.bs.dropdown', this.removeActiveClassFromDropdown);
  }

  /**
   * Sets isDropdownActive to true
   */
  private setActiveClassToDropdown = () => {
    this.isDropdownActive = true;
  };

  /**
   * Sets isDropdownActive to false
   */
  private removeActiveClassFromDropdown = () => {
    this.isDropdownActive = false;
  };
}
